import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MessageSquareText, Plus, X, Clock, Loader2 } from 'lucide-react';

/**
 * Left-hand history sidebar for KMS Smart.
 *
 * Lists every past analysis the signed-in user has run, grouped into
 * Today / Yesterday / Last 7 days / Last 30 days / Older buckets. Each item
 * uses the user's original `profile_text` as the conversation title.
 *
 * Purely a picker — it emits `onSelect(analysisId)` when an item is clicked
 * and `onStartNew()` when the + button is pressed. The parent page owns the
 * read-only view state.
 *
 * Responsive:
 *   - Desktop: render as a persistent sidebar column.
 *   - Mobile : pass `isOpen` / `onClose` to render as a slide-in drawer.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bucketing rules — bucket order drives render order.
const BUCKETS = [
  { key: 'today', label: 'Today', maxDays: 0 },
  { key: 'yesterday', label: 'Yesterday', maxDays: 1 },
  { key: 'week', label: 'Last 7 days', maxDays: 7 },
  { key: 'month', label: 'Last 30 days', maxDays: 30 },
  { key: 'older', label: 'Older', maxDays: Infinity },
];

function startOfUtcDay(d) {
  const x = new Date(d);
  x.setUTCHours(0, 0, 0, 0);
  return x;
}

function bucketFor(createdAt) {
  if (!createdAt) return 'older';
  const now = startOfUtcDay(new Date());
  const then = startOfUtcDay(new Date(createdAt));
  const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays <= 7) return 'week';
  if (diffDays <= 30) return 'month';
  return 'older';
}

function truncate(text, n = 64) {
  if (!text) return 'Untitled conversation';
  const clean = text.trim().replace(/\s+/g, ' ');
  return clean.length > n ? `${clean.slice(0, n)}…` : clean;
}

const KmsChatHistorySidebar = ({
  userId,
  activeAnalysisId,
  onSelect,
  onStartNew,
  isOpen = true,
  onClose,
  refreshKey = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!userId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr('');
    axios
      .get(`${API_URL}/api/impact-analyser/user-analyses/${userId}`, {
        params: { limit: 100 },
      })
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.analyses || []).filter((a) => !!a.id);
        setItems(list);
      })
      .catch(() => {
        if (!cancelled) setErr('Could not load history');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [userId, refreshKey]);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
    items.forEach((it) => {
      map[bucketFor(it.created_at)].push(it);
    });
    return map;
  }, [items]);

  const body = (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <MessageSquareText className="h-4 w-4 text-emerald-700" />
          <h3 className="text-sm font-semibold text-slate-900 tracking-tight">
            History
          </h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onStartNew}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5 transition"
            data-testid="sidebar-start-new-chat"
            aria-label="Start new chat"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden rounded-full p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Close history"
              data-testid="sidebar-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* List — scrollable. A fixed bottom cap ("End of history") sits below the
          scroll area so there's always a visible terminator before the footer. */}
      <div className="flex-1 overflow-y-auto px-2 py-3" data-testid="history-list">
        {loading && (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-xs">Loading…</span>
          </div>
        )}

        {!loading && err && (
          <div className="px-3 py-6 text-xs text-slate-500">{err}</div>
        )}

        {!loading && !err && items.length === 0 && (
          <div className="px-3 py-10 text-center">
            <div className="mx-auto w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <Clock className="h-4 w-4 text-emerald-700" />
            </div>
            <p className="text-xs text-slate-600 font-medium mb-1">
              No past conversations yet
            </p>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Your KMS Smart analyses will appear here.
            </p>
          </div>
        )}

        {!loading &&
          !err &&
          BUCKETS.map((b) => {
            const rows = grouped[b.key] || [];
            if (!rows.length) return null;
            return (
              <div key={b.key} className="mb-4" data-testid={`history-group-${b.key}`}>
                <div className="px-2 mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  {b.label}
                </div>
                <ul className="space-y-0.5">
                  {rows.map((it) => {
                    const active = it.id === activeAnalysisId;
                    return (
                      <li key={it.id}>
                        <button
                          onClick={() => onSelect(it.id)}
                          className={`group w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                            active
                              ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                          data-testid={`history-item-${it.id}`}
                        >
                          <div className="line-clamp-2 leading-snug">
                            {truncate(it.profile_text, 72)}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400 min-w-0 whitespace-nowrap">
                            <span className="flex-shrink-0">{it.schemes_count || 0} schemes</span>
                            {it.total_benefit && (
                              <>
                                <span className="flex-shrink-0">·</span>
                                <span className="truncate min-w-0">{it.total_benefit}</span>
                              </>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

        {/* In-list end marker — only shown when we have items, so the list
            has a clear terminator and doesn't look like it runs into the footer. */}
        {!loading && !err && items.length > 0 && (
          <div
            className="mt-3 pt-3 pb-1 text-center text-[10px] uppercase tracking-[0.2em] text-slate-300"
            data-testid="history-end-marker"
          >
            — end of history —
          </div>
        )}
      </div>

      {/* Solid bottom cap — sits OUTSIDE the scroll area so it's always pinned
          to the sidebar's bottom edge and provides a hard visual terminator
          before the site footer begins. */}
      <div
        className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-[10px] text-slate-400 flex items-center justify-between flex-shrink-0"
        data-testid="history-bottom-cap"
      >
        <span>
          {items.length > 0 ? `${items.length} past ${items.length === 1 ? 'conversation' : 'conversations'}` : ''}
        </span>
        <MessageSquareText className="h-3 w-3 text-slate-300" />
      </div>
    </div>
  );

  // Desktop: plain column. Mobile: slide-in drawer.
  return (
    <>
      {/* Desktop column — flush-left, sticky below header so it stays in view while the page scrolls */}
      <aside
        className="hidden lg:flex w-72 flex-shrink-0 border-r border-slate-200 bg-slate-50 sticky top-[4.5rem] self-start h-[calc(100vh-4.5rem)]"
        data-testid="kms-history-sidebar-desktop"
      >
        {body}
      </aside>

      {/* Mobile drawer */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="kms-history-sidebar-mobile">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
            aria-hidden
          />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-xs bg-white shadow-2xl">
            {body}
          </div>
        </div>
      )}
    </>
  );
};

export default KmsChatHistorySidebar;
